import {
  ExclamationCircleOutlined,
  MenuOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  Channel,
  ErrorResponse,
  GET_CHANNEL_BY_ID,
  PATCH_PHASE_ORDER,
  Phase,
} from '@frontend/data-access';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DroppableProvided,
  DropResult,
} from '@hello-pangea/dnd';
import { App, Button, List, Spin } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import IdeaChannelCreatePhase from '../../idea-channel-create-phase/idea-channel-create-phase';
import { TabHeader } from '../components';

function PhaseSettings() {
  const { notification } = App.useApp();
  const { t } = useTranslation('common');
  const router = useRouter();
  const { id } = router.query;
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [currentPhase, setCurrentPhase] = useState<Phase>();
  const [dataSource, setDataSource] = useState<Phase[]>([]);

  const { data, loading, refetch } = useQuery<{
    channel: Channel;
  }>(GET_CHANNEL_BY_ID, {
    variables: { id },
  });

  const handleError = (error: ErrorResponse) => {
    notification.error({
      message: t('error'),
      description: error.message,
      icon: (
        <span className="text-red-600">
          <ExclamationCircleOutlined />
        </span>
      ),
    });
  };

  const [patchPhaseOrder, { loading: patchLoading }] = useMutation(
    PATCH_PHASE_ORDER,
    {
      onError: handleError,
      onCompleted() {
        notification.success({
          message: t('phase_order_updated_successfully'),
        });
        window.location.reload();
      },
    }
  );

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(dataSource);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setDataSource(items);
    patchPhaseOrder({
      variables: {
        ids: items.map((item) => item.id),
      },
    });
  };

  const handleModalOpen = (phase?: Phase) => {
    phase && setCurrentPhase(phase);
    setIsOpenModal(true);
  };

  const handleModalClose = () => {
    setIsOpenModal(false);
    setCurrentPhase(undefined);
  };

  const handleCompleted = () => {
    handleModalClose();
  };

  useEffect(() => {
    if (data?.channel.channelPhases) {
      setDataSource(data.channel.channelPhases.map((item) => item.phase));
    }
  }, [data]);

  const renderItem = (phase: Phase, index: number) => (
    <Draggable key={phase.id} draggableId={phase.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`
            bg-white rounded-lg border border-gray-400 mb-3 last:mb-0
            ${snapshot.isDragging ? '' : ''}
          `}
          data-test="phase-card"
        >
          <div className="px-4 py-3">
            <div className="flex items-center gap-4">
              <div
                {...provided.dragHandleProps}
                className="text-gray-800 cursor-move hover:text-gray-700"
              >
                <MenuOutlined />
              </div>
              <div className="w-full">
                <div className="flex items-center justify-between gap-4">
                  <h3 className="text-sm font-medium text-gray-800 truncate mb-0">
                    {phase.title}
                  </h3>
                  <div>
                    <Button
                      type="link"
                      onClick={() => handleModalOpen(phase)}
                      className="flex-shrink-0"
                      data-test="edit-phase-btn"
                    >
                      {t('edit')}
                    </Button>
                  </div>
                </div>
                <span className="text-sm text-gray-700 line-clamp-3">
                  {phase.description}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <TabHeader title={t('phases')} description={t('phases_description')} />
      <div className="space-y-6">
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="phases">
            {(provided: DroppableProvided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="relative"
              >
                <List
                  dataSource={dataSource}
                  renderItem={renderItem}
                  loading={loading || patchLoading}
                  locale={{
                    emptyText: t('no_phases_found'),
                  }}
                />
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <Button
          shape="round"
          icon={<PlusOutlined />}
          onClick={() => handleModalOpen()}
          data-test="add-phase-btn"
        >
          {t('add_more_phase')}
        </Button>
      </div>
      <IdeaChannelCreatePhase
        open={isOpenModal}
        phase={currentPhase}
        onFinish={handleCompleted}
        onCancel={handleModalClose}
        refetch={refetch}
      />
    </div>
  );
}

export default PhaseSettings;
